/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  format,
  formatDistance,
  formatDistanceToNow,
  startOfDay,
  subDays,
  subMinutes,
  addDays,
  addMinutes,
  isSameDay,
  isValid,
  differenceInDays,
  differenceInMinutes,
  differenceInBusinessDays,
  differenceInSeconds,
} from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import i18n from 'i18n/config';
import { AnyObject } from 'types';

type AcceptableDate = string | Date;

export {
  subDays,
  subMinutes,
  addDays,
  addMinutes,
  differenceInBusinessDays,
  isSameDay,
  differenceInDays,
  differenceInMinutes,
  differenceInSeconds,
};

function toDate(date: AcceptableDate): Date {
  return new Date(date);
}

const dateFormats = {
  'dd/mm/yyyy': "dd'/'MM'/'yyyy",
  'yyyy-mm-dd': "yyyy'-'MM'-'dd",
  // PPP = date written version => https://date-fns.org/v4.1.0/docs/format check for more
} as const;

type DateFormat = keyof typeof dateFormats;

/**
 * Formats dates to specific formats
 *
 * IMPORTANT: Be aware of TZ conversions
 *
 * ===> new Date('2024-10-11') => On UTC-3 (SP timezone) results in 2024-10-10 21:00hrs
 *
 * ===> Which would then be converted with the format. CAREFUL!!
 */
export function formatDate(date: AcceptableDate, pattern: DateFormat = 'dd/mm/yyyy'): string {
  return format(toDate(date), dateFormats[pattern]);
}

type Languages = 'en' | 'pt' | 'es';

const languageToLocale = {
  pt: ptBR,
  en: enUS,
  es,
};

const languageToLocaleString = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es',
};

export function getTimePassed(date: string | Date): string {
  const distanceDate = typeof date === 'string' ? new Date(date) : date;

  const timePassed = formatDistance(distanceDate, new Date(), {
    addSuffix: true,
    locale: languageToLocale[i18n.language as Languages],
  });

  return timePassed;
}

/**
 * Gets YYYY-MM-DD date
 */
export function getIsoDatePart(date: AcceptableDate): string {
  const isoString = typeof date === 'string' ? date : date.toISOString();

  return isoString.slice(0, 10);
}

/**
 * Formats the date to the "Sunday, November 19, 2023" format
 */
export function getReadableDate(date: AcceptableDate): string {
  return new Intl.DateTimeFormat(languageToLocaleString[i18n.language as Languages], {
    dateStyle: 'full',
  }).format(toDate(date));
}

/**
 * Formats the date to the "09 JAN" format
 */
export function getShortDayMonth(date: AcceptableDate) {
  return new Intl.DateTimeFormat(languageToLocaleString[i18n.language as Languages], {
    day: '2-digit',
    month: 'short',
  }).format(toDate(date));
}

/**
 * Formats the date to the "JAN" format
 */
export function getShortMonth(date: AcceptableDate) {
  return new Intl.DateTimeFormat(languageToLocaleString[i18n.language as Languages], {
    month: 'short',
  }).format(toDate(date));
}

/**
 * 2024 => 24
 */
export function getTwoDigitYear(date: AcceptableDate) {
  return new Intl.DateTimeFormat(languageToLocaleString[i18n.language as Languages], {
    year: '2-digit',
  }).format(toDate(date));
}

/**
 * Formats the date to the "November 19, 2023" format
 */
export function getDateWithMonthText(date: Date): string {
  return new Intl.DateTimeFormat(languageToLocaleString[i18n.language as Languages], {
    dateStyle: 'long',
  }).format(date);
}

export function getLocalDateString(
  date: Date,
  { accountTimezone = false }: { accountTimezone?: boolean } = { accountTimezone: false },
): string {
  const timezoneOffset = new Date().getTimezoneOffset();

  // this makes sure we can reference 'expected dates' and such properly
  // on our DB and show them independant of TZ on frontend
  return subMinutes(date, accountTimezone ? timezoneOffset : 0)
    .toISOString()
    .replace('Z', '');
}

export function extractDateString(date: Date | string): string {
  const ref = typeof date === 'string' ? new Date(date) : date;

  const month = `${ref.getMonth() + 1}`.padStart(2, '0');
  const day = `${ref.getDate()}`.padStart(2, '0');

  return `${ref.getFullYear()}-${month}-${day}`;
}

export function getDateToLocaleStartOfDay(date?: Date): string {
  const actualDate = date || new Date();

  const start = startOfDay(actualDate);

  return getLocalDateString(start, {
    accountTimezone: true,
  });
}

/**
 * Formats the date to a dd/mm/yyyy hh:mm structure,
 * respecting 12/24hr format depending on language, but not date order
 */
export function getTimestampDisplay(timestamp: string): string {
  const date = new Date(timestamp);

  return `${formatDate(date, 'dd/mm/yyyy')} ${new Intl.DateTimeFormat(
    languageToLocaleString[i18n.language as Languages],
    {
      timeStyle: 'short',
    },
  ).format(date)}`;
}

export function getTimeDisplay(timestamp: string): string {
  const date = new Date(timestamp);

  return new Intl.DateTimeFormat('pt', {
    timeStyle: 'short',
  }).format(date);
}

export const dateDistanceToNow = (date: string): string => {
  const publishedAtDistanceToNow = formatDistanceToNow(Date.parse(date), {
    addSuffix: true,
    locale: languageToLocale[i18n.language as Languages],
  });

  return publishedAtDistanceToNow;
};

export const formatPublishedAtDate = (date: string): string => {
  const publishedAtDateFormatted = format(Date.parse(date), "LLLL d, y '-' HH:mm", {
    locale: languageToLocale[i18n.language as Languages],
  });

  return publishedAtDateFormatted;
};

export function getInitialDateSliceParams(SIZE_IN_DAYS = 30): { startDate: Date; endDate: Date } {
  const date = new Date(getDateToLocaleStartOfDay());

  return {
    startDate: subDays(date, SIZE_IN_DAYS),
    endDate: date,
  };
}

/**
 *
 * @param dateString A date string in the yyyy-mm-dd format
 * @returns The Date object correctly set to the start of the given day
 *
 * Pure JS does not work as you might think:
 * ===> new Date('2024-10-11') => On UTC-3 (SP timezone) results in 2024-10-10 21:00hrs
 */
export function fromYYYYmmDD(dateString: string) {
  return new Date(`${dateString}T00:00:00`);
}

/**
 *
 * @param dateString The date to validate is in the yyyy-mm-dd format
 * @returns true/false
 */
export function isValidYYYYmmDD(dateString: string) {
  try {
    // throws on bad date
    formatDate(fromYYYYmmDD(dateString), 'dd/mm/yyyy');

    return true;
  } catch (err) {
    return false;
  }
}

/**
 *
 * @param values An object containing one or more yyyy-mm-dd date strings
 * @param target The keys to revive and return as dates
 * @returns An object that contains the successfully revived dates
 */
export function reviveDates<T extends Record<string, string>, Target extends keyof T = keyof T>(
  values: T,
  target?: Target[],
): { [Key in Target]: Date } {
  const actualTarget = target ?? (Object.keys(values) as Target[]);

  const revived = Object.fromEntries(
    actualTarget
      .filter((key) => isValidYYYYmmDD(values[key]))
      .map((key) => [key, fromYYYYmmDD(values[key])]),
  );

  return revived as { [Key in Target]: Date };
}

type DateKeys<T extends AnyObject> = NonNullable<
  { [Key in keyof T]: T[Key] extends Date ? Key : null }[keyof T]
>;

/**
 *
 * @param values An object containing one or more dates
 * @param target The keys to revive and return as YYYY-MM-DD dates
 * @returns An object that contains the successfully serialized dates
 */
export function serializeDates<T extends AnyObject, Target extends DateKeys<T> = DateKeys<T>>(
  values: T,
  target?: Target[],
): { [Key in Target]: string } {
  const actualTarget = target ?? (Object.keys(values) as Target[]);

  const revived = Object.fromEntries(
    actualTarget
      .filter((key) => (values[key] as any) instanceof Date)
      .map((key) => [key, formatDate(values[key], 'yyyy-mm-dd')]),
  );

  return revived as { [Key in Target]: string };
}

const formatRevivers: Record<DateFormat, (s: string) => Date> = {
  'yyyy-mm-dd': (date) => new Date(`${date}T00:00:00.000`),

  'dd/mm/yyyy'(date) {
    return this['yyyy-mm-dd'](date.split('/').reverse().join('-'));
  },
};

/**
 * Revives a date string correctly aware of the user TZ
 *
 * ===> new Date('2024-10-11') => On UTC-3 (SP timezone) results in 2024-10-10 21:00hrs
 *
 * ===> dateFromFormat('2024-10-11', 'yyyy-mm-dd') => Date obj on correct date
 */
export function dateFromFormat(target: string, format: DateFormat) {
  const date = formatRevivers[format](target);

  if (!isValid(date)) throw Error(`Bad date format ${target} format:${format}`);

  return date;
}
